import { createAsyncThunk } from '@reduxjs/toolkit';
import DomPurify from 'dompurify';
import { activeContentPages, CHAT_BLK, SUMMARIZE_PROMPT } from '../constants';
import { ChatBlkQueryService, ChatBlkTelemetryService } from '../services';
import {
    ChatblkMfeFunctionMap,
    ChatBlkQuery,
    ChatBlkQueryScopeEnum,
    ChatBlkRelationshipManager,
    QueryState,
    QueryTypeEnum,
    telemetryFunctionMap,
} from '../types';
import { uuidv4 } from '../utils';
import { chatFrameActions } from './chatFrameSlice';
import { queryActions } from './querySlice';
import type { ChatBlkState } from './store';

export const fetchRecentPrompts = createAsyncThunk<
    { prompt: string; timestamp: string }[],
    void,
    { state: ChatBlkState }
>('querySlice/fetchRecentPrompts', async () => {
    const current = new Date();
    const minDate: Date = new Date(current);
    minDate.setDate(minDate.getDate() - 30);
    try {
        const recentPrompts =
            await ChatBlkQueryService.getInstance()?.fetchRecentQueries(
                minDate
            );
        return recentPrompts || [];
    } catch (error) {
        console.error(`[${CHAT_BLK}]Error fetching recent prompts:`, error);
        throw error;
    }
});

export const fetchRelationshipManagers = createAsyncThunk<
    ChatBlkRelationshipManager[],
    void,
    { state: ChatBlkState }
>('querySlice/fetchRelationshipManagers', async () => {
    const relationshipManagers =
        await ChatBlkQueryService.getInstance()?.fetchRelationshipManagers();
    return relationshipManagers;
});

// TODO: Re-enable login thunk after 2025.10 release
// export const login = createAsyncThunk<
//     { email?: string; username?: string },
//     { clickLoggedIn?: boolean },
//     { state: ChatBlkState }
// >(
//     'authSlice/login',
//     async (
//         { clickLoggedIn = false },
//         { getState, dispatch, rejectWithValue }
//     ) => {
//         const {
//             replace,
//             location: { hostname },
//             BLK: { isCwpUserSignedIn } = { isCwpUserSignedIn: false },
//             globalAnalyticsParams = {},
//         } = window as unknown as {
//             replace: (path: string) => void;
//             location: { hostname: string };
//             BLK: { isCwpUserSignedIn: boolean };
//             globalAnalyticsParams: { [key: string]: string };
//         };
//         logger.info(`Logging in...`, {
//             clickLoggedIn,
//             isCwpUserSignedIn,
//             hostname,
//         });

//         const { PRE_PROD_HOSTS, PRE_PROD_LOGIN_PATH, PROD_LOGIN_PATH } = URIS;
//         // Includes localhost and and ChatBLK demo site env
//         const isPreProd = PRE_PROD_HOSTS.includes(hostname);
//         // for pre-prod environments, users need to click the login button to be authenticated
//         if ((!clickLoggedIn && isPreProd) || getState().authSlice.isLoggedIn)
//             return;

//         try {
//             const axiosInstance = getAxiosInstance(
//                 (isPreProd ? getApiBaseRef : getHost)(),
//                 DEFAULT.HEADERS
//             );
//             if (isCwpUserSignedIn && clickLoggedIn) {
//                 const queriedSigninPath = document
//                     .querySelector('#signinBlock a')
//                     ?.getAttribute('href');

//                 replace(
//                     queriedSigninPath ||
//                         (hostname.startsWith('blk-one01')
//                             ? PRE_PROD_LOGIN_PATH
//                             : PROD_LOGIN_PATH)
//                 );
//                 logger.info(`Redirecting to signin path...`);
//                 return { email: '', username: '' };
//             } else {
//                 dispatch(chatFrameActions.showLoading());
//                 const iframeUrl = `https://${hostname}/financial-professionals/financial-professionals/advisorcenter-login`;
//                 const response = await axiosInstance.get(
//                     isPreProd ? BLK_API_PATHS.BMS : iframeUrl,
//                     {
//                         withCredentials: true,
//                         headers: {
//                             Accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
//                         },
//                     }
//                 );

//                 logger.info(`🔍 Response Status:`, response.status);
//                 logger.info(
//                     `🔍 Response OK:`,
//                     response.status >= 200 && response.status < 300
//                 );
//                 logger.info(` Response Headers:`, response.headers);

//                 if (response.status === 401) {
//                     logger.info('❌ User is NOT verified (401 Unauthorized)');
//                     dispatch(chatFrameActions.showUnverified());
//                 } else if (response.status === 200) {
//                     logger.info('✅ User is verified (200 OK)');
//                     dispatch(chatFrameActions.loggedIn());
//                 } else {
//                     logger.info(`⚠️ Unexpected status: ${response.status}`);
//                     dispatch(chatFrameActions.showLoadingError());
//                 }
//                 const {
//                     userFirstName = 'Blake',
//                     userLastName = 'Rocco',
//                     emailAddress = '',
//                 } = globalAnalyticsParams;
//                 const username =
//                     userFirstName && userLastName
//                         ? `${userFirstName} ${userLastName}`
//                         : emailAddress;
//                 const email = emailAddress || username;

//                 dispatch(chatFrameActions.loggedIn());
//                 dispatch(fetchRelationshipManagers());
//                 dispatch(fetchRecentPrompts());

//                 // If the user had the panel open before login and/or asked a related question from
//                 // host app, we open the panel after login and send the related question once authentication is done.
//                 if (getCacheValue(USED_CHAT_BLK_LOGIN_ENTRYPOINT)) {
//                     getCacheValue('action') === CHAT_BLK_TOGGLE_EVENT_OPEN &&
//                         dispatch(chatFrameActions.openPanel());
//                 }
//                 clearChatCache(['action', 'relatedQuestion', 'summarize']);
//                 return { email, username };
//             }
//         } catch (error) {
//             logger.error('❌ Error authenticating user:', error);
//             ChatBlkTelemetryService.trackError({
//                 userHasCopilotAccess: false,
//                 unableToPerformAuth: !ChatBlkBaseService.getInstance(),
//                 message: ChatBlkBaseService.getInstance()
//                     ? UNABLE_TO_PERFORM_AUTHORIZATION_SECONDARY_MESSAGE
//                     : UNAUTHORIZED_SECONDARY_MESSAGE,
//             });
//             dispatch(
//                 chatFrameActions.setErrorMessage(
//                     error instanceof Error ? error.message : 'Unknown error'
//                 )
//             );
//             dispatch(chatFrameActions.showLoadingError());
//             return rejectWithValue(false);
//         }
//     }
// );

/**
 * Sends a query to the chat service and handles the response.
 * @param prompt - A string containing the prompt for the query.
 * @param telemetryFunction - An optional function from the telemetry function map to track the event.
 * @param queryType - An optional type of query being sent. Can be 'query', 'summarize', or 'contact-rm'.
 * @param chatExchangeId - The unique identifier for the chat block event.
 * @returns A promise that resolves to a partial ChatBlkQuery object containing the response data.
 */
export const sendQuery = createAsyncThunk<
    Partial<ChatBlkQuery>,
    {
        prompt: string;
        telemetryFunction?: ChatblkMfeFunctionMap[keyof ChatblkMfeFunctionMap];
        queryType?: QueryTypeEnum;
        chatExchangeId?: string;
        hashId?: string;
    },
    { state: ChatBlkState }
>(
    'querySlice/sendQuery',
    async (
        {
            prompt,
            telemetryFunction,
            queryType = QueryTypeEnum.QUERY,
            chatExchangeId = uuidv4(),
            hashId = uuidv4(),
        },
        { dispatch, rejectWithValue, getState }
    ) => {
        const type =
            prompt === SUMMARIZE_PROMPT
                ? QueryTypeEnum.SUMMARIZE
                : queryType || QueryTypeEnum.QUERY;
        const queryStartTime = Date.now();
        const chatService = ChatBlkQueryService.getInstance();
        const { cancelQuery, fetchQueryResponse, fetchSummary } = chatService;
        cancelQuery();
        if (
            getState().chatFrameSlice.activeContent !== activeContentPages.CHAT
        ) {
            dispatch(
                chatFrameActions.setActiveContent(activeContentPages.CHAT)
            );
        }

        telemetryFunction && ChatBlkTelemetryService.track(telemetryFunction);

        const purifiedPrompt = DomPurify.sanitize(
            queryType === QueryTypeEnum.SUMMARIZE ? SUMMARIZE_PROMPT : prompt
        );

        try {
            let apiResponse: Partial<ChatBlkQuery>;
            if (queryType !== QueryTypeEnum.CONTACT_RM) {
                apiResponse =
                    purifiedPrompt === SUMMARIZE_PROMPT
                        ? await fetchSummary(window.location.href)
                        : await fetchQueryResponse(
                              purifiedPrompt,
                              chatExchangeId
                          );
            } else {
                const relationshipManagers =
                    await ChatBlkQueryService.getInstance().fetchRelationshipManagers();
                apiResponse = {
                    output: {
                        metadata: {
                            relationshipManagers:
                                Array.isArray(relationshipManagers) &&
                                relationshipManagers.length > 0
                                    ? relationshipManagers
                                    : getState().querySlice
                                          .relationshipManagers,
                            additionalInfo: undefined,
                            relationshipManagersIncluded: true,
                        },
                        sections: [],
                    },
                    followUpPrompts: [],
                    response: `Here is your ${prompt} contact information:`,
                    sources: [],
                    id: uuidv4(),
                    sessionId: chatService.sessionId,
                };
            }
            const responseTimestamp = new Date().toISOString();
            if (apiResponse) {
                dispatch(
                    queryActions.updateQuery({
                        hashId,
                        updatedProps: {
                            ...apiResponse,
                            responseTimestamp,
                            state: QueryState.LOADED,
                            hashId,
                            scope:
                                apiResponse.scope ||
                                (ChatBlkQueryScopeEnum.UNSPECIFIED as ChatBlkQueryScopeEnum),
                            chatExchangeId,
                        },
                        queryType: type,
                    })
                );
                const queryEndTime = Date.now();
                ChatBlkTelemetryService.track(
                    telemetryFunctionMap.CHATBLK_MFE_FUNCTION_QUERY_RESPONSE_LOADED,
                    queryEndTime - queryStartTime,
                    apiResponse.id
                );
                dispatch(fetchRecentPrompts());
                return {
                    ...apiResponse,
                    hashId,
                    query: purifiedPrompt,
                    responseTimestamp,
                    state: QueryState.LOADED,
                    scope:
                        apiResponse.scope ||
                        (ChatBlkQueryScopeEnum.UNSPECIFIED as ChatBlkQueryScopeEnum),
                    chatExchangeId,
                    queryType: type,
                };
            }
        } catch (error) {
            const queryEndTime = Date.now();
            const { name, message } = error as Error;
            ChatBlkTelemetryService.trackError({
                message: 'Query responded with error',
                error,
                elapsedTime: queryEndTime - queryStartTime,
            });
            if (error.name !== 'AbortError' && error.name !== 'CancelError') {
                dispatch(
                    chatFrameActions.setErrorMessage(
                        name && message ? `${name}: ${message}` : 'undefined'
                    )
                );
                return rejectWithValue(
                    name && message ? `${name}: ${message}` : 'undefined'
                );
            }
            return rejectWithValue('undefined');
        }
    }
);
